import { Box, Typography } from '@mui/material';
import { useMemo, useState, useCallback } from 'react';
import { graphConnect, sugiyama } from 'd3-dag';
import { ROUTE_PATH_COLORS, EDGE_OFFSET_SPACING, getEdgeGeometry } from '../constants/routeGraphStyle';
import { EdgeWithArrow } from './RouteGraphPrimitives';
import type { PathSegment, RouteStructure, TrailerPath } from '../utils/routeInference';
import { getSegmentNodeId } from '../utils/routeInference';

/**
 * Layout node id: differentiates source vs dest when the same storage is used for both.
 * Use storage:source:id at first segment, storage:dest:id at last segment; otherwise getSegmentNodeId(seg).
 */
function getSegmentNodeIdForLayout(seg: PathSegment, segmentIndex: number, pathLength: number): string {
    if (seg.kind === 'storage') {
        if (segmentIndex === 0) return `storage:source:${seg.storageLocationId}`;
        if (segmentIndex === pathLength - 1) return `storage:dest:${seg.storageLocationId}`;
    }
    return getSegmentNodeId(seg);
}

const NODE_MIN_WIDTH = 80;
const NODE_HEIGHT = 28;
const NODE_PADDING_X = 12;
const CHAR_WIDTH_EST = 6;
const NODE_MAX_WIDTH = 280;
const HORIZONTAL_GAP = 18;
const VERTICAL_GAP = 36;
const SVG_PADDING = 10;
const TOOLTIP_LINE_HEIGHT = 24; /* reserve space so hover doesn't change layout */

function nodeWidthForLabel(label: string): number {
    const w = NODE_PADDING_X * 2 + label.length * CHAR_WIDTH_EST;
    return Math.min(NODE_MAX_WIDTH, Math.max(NODE_MIN_WIDTH, w));
}

/** Node id is either an event id or storage:storageLocationId */
export interface RouteMapNode {
    id: string;
    label: string;
    startDate?: Date | null;
    endDate?: Date | null;
}

export interface RouteMapVisualizationProps {
    /** Nodes in display order (e.g. source storage, event1, event2, dest storage). If not provided, derived from routeStructure. */
    nodes?: RouteMapNode[];
    /** Inferred route structure (trailer paths). If provided, edges are drawn per path; otherwise one path through nodes. */
    routeStructure?: RouteStructure | null;
    /** Resolve node id (eventId or storage:storageLocationId) to display label */
    getNodeLabel: (nodeId: string) => string;
    getTrailerName?: (trailerId: string) => string;
    onNodeClick?: (nodeId: string) => void;
    onEdgeClick?: (trailerId: string, fromId: string, toId: string) => void;
}

/** Map node id -> { width, height } for layout and render */
function getNodeSizes(nodes: RouteMapNode[]): Map<string, { width: number; height: number }> {
    const map = new Map<string, { width: number; height: number }>();
    for (const n of nodes) {
        map.set(n.id, { width: nodeWidthForLabel(n.label), height: NODE_HEIGHT });
    }
    return map;
}

function formatDateRange(start?: Date | null, end?: Date | null): string {
    if (!start && !end) return '';
    if (!end) return start ? new Date(start).toLocaleDateString() : '';
    if (!start) return new Date(end).toLocaleDateString();
    return `${new Date(start).toLocaleDateString()} – ${new Date(end).toLocaleDateString()}`;
}

/**
 * SVG-based interactive route map: nodes for locations, edges for movements.
 * Hover for details; click nodes to view events, click edges for trailer info.
 * Wrapped in horizontal scroll on narrow viewports so the full graph remains visible.
 */
export default function RouteMapVisualization({
    nodes: nodesProp,
    routeStructure,
    getNodeLabel,
    getTrailerName = (id: string) => `Trailer ${id.slice(0, 8)}`,
    onNodeClick,
    onEdgeClick,
}: RouteMapVisualizationProps) {
    const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined);
    /** Leg (fromId, toId) being hovered; tooltip shows all trailers on that leg */
    const [hoveredLeg, setHoveredLeg] = useState<{ fromId: string; toId: string } | null>(null);

    const nodes: RouteMapNode[] = useMemo(() => {
        if (nodesProp && nodesProp.length > 0) return nodesProp;
        if (routeStructure && routeStructure.trailerPaths.size > 0) {
            const seen = new Set<string>();
            const out: RouteMapNode[] = [];
            for (const path of Array.from(routeStructure.trailerPaths.values())) {
                const len = path.segments.length;
                path.segments.forEach((seg: PathSegment, i: number) => {
                    const id = getSegmentNodeIdForLayout(seg, i, len);
                    if (seen.has(id)) return;
                    seen.add(id);
                    out.push({
                        id,
                        label: getNodeLabel(id),
                        startDate: seg.startDate,
                        endDate: seg.endDate,
                    });
                });
            }
            return out;
        }
        return [];
    }, [nodesProp, routeStructure, getNodeLabel]);

    const pathsForEdges = useMemo((): TrailerPath[] => {
        if (routeStructure && routeStructure.trailerPaths.size > 0) {
            const paths: TrailerPath[] = [];
            for (const path of Array.from(routeStructure.trailerPaths.values())) paths.push(path);
            return paths;
        }
        if (nodes.length >= 2) {
            const baseDate = new Date(0);
            const segments = nodes.map((n, i) => {
                const startDate = n.startDate ?? new Date(baseDate.getTime() + i * 86400000);
                const endDate = n.endDate ?? new Date(baseDate.getTime() + (i + 1) * 86400000);
                if (n.id.startsWith('storage:')) {
                    return { kind: 'storage' as const, storageLocationId: n.id.slice(8), startDate, endDate };
                }
                return { kind: 'event' as const, eventId: n.id, startDate, endDate };
            });
            return [{ trailerId: '__single__', segments }];
        }
        return [];
    }, [routeStructure, nodes]);

    /** Unique legs (fromId → toId) with list of trailer IDs using that leg, for one edge per leg + tooltip */
    const legsWithTrailers = useMemo(() => {
        const map = new Map<string, { fromId: string; toId: string; trailerIds: string[] }>();
        for (const path of pathsForEdges) {
            const len = path.segments.length;
            for (let i = 0; i < len - 1; i++) {
                const fromId = getSegmentNodeIdForLayout(path.segments[i], i, len);
                const toId = getSegmentNodeIdForLayout(path.segments[i + 1], i + 1, len);
                const key = `${fromId}\t${toId}`;
                const existing = map.get(key);
                if (existing) {
                    if (path.trailerId !== '__single__' && !existing.trailerIds.includes(path.trailerId)) {
                        existing.trailerIds.push(path.trailerId);
                    }
                } else {
                    map.set(key, {
                        fromId,
                        toId,
                        trailerIds: path.trailerId === '__single__' ? [] : [path.trailerId],
                    });
                }
            }
        }
        const result: Array<{ fromId: string; toId: string; trailerIds: string[] }> = [];
        for (const v of Array.from(map.values())) result.push(v);
        return result;
    }, [pathsForEdges]);

    /**
     * Build DAG links from routeStructure (or from nodes for single path), then run d3-dag sugiyama layout.
     * Returns positions (center x,y) and dimensions (width, height) for rectangular nodes.
     */
    const nodeSizes = useMemo(() => getNodeSizes(nodes), [nodes]);
    const nodePositions = useMemo(() => {
        const baseX = SVG_PADDING + NODE_MAX_WIDTH / 2;
        const baseY = SVG_PADDING + NODE_HEIGHT / 2;

        let links: Array<[string, string]> = [];

        if (routeStructure && routeStructure.trailerPaths.size > 0) {
            const seen = new Set<string>();
            for (const path of Array.from(routeStructure.trailerPaths.values())) {
                const len = path.segments.length;
                for (let i = 0; i < len - 1; i++) {
                    const a = getSegmentNodeIdForLayout(path.segments[i], i, len);
                    const b = getSegmentNodeIdForLayout(path.segments[i + 1], i + 1, len);
                    const key = `${a}\t${b}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        links.push([a, b]);
                    }
                }
            }
        } else if (nodes.length >= 2) {
            for (let i = 0; i < nodes.length - 1; i++) {
                links.push([nodes[i].id, nodes[i + 1].id]);
            }
        }

        const defaultSize = { width: NODE_MIN_WIDTH, height: NODE_HEIGHT };
        const getSize = (id: string) => nodeSizes.get(id) ?? defaultSize;

        if (links.length === 0) {
            // No edges: single node or empty – linear position with rectangular spacing
            let x = baseX;
            return nodes.map(n => {
                const size = getSize(n.id);
                const cx = x + size.width / 2;
                x += size.width + HORIZONTAL_GAP;
                return { id: n.id, x: cx, y: baseY, width: size.width, height: size.height };
            });
        }

        try {
            const connect = graphConnect();
            const dag = connect(links);
            const layout = sugiyama()
                .nodeSize((node: { data: string }) => {
                    const size = getSize(node.data);
                    return [size.width, size.height];
                })
                .gap([HORIZONTAL_GAP, VERTICAL_GAP]);
            // .tweaks([tweakFlip('diagonal')]); // swap x/y so flow is left→right (horizontal)
            layout(dag);

            const positions: Array<{ id: string; x: number; y: number; width: number; height: number }> = [];
            Array.from(dag.nodes()).forEach((node: { data: string; x: number; y: number }) => {
                const size = getSize(node.data);
                positions.push({
                    id: node.data,
                    x: baseX + node.x,
                    y: baseY + node.y,
                    width: size.width,
                    height: size.height,
                });
            });
            return positions;
        } catch {
            // If d3-dag fails (e.g. cycle), fall back to linear layout
            let x = baseX;
            return nodes.map(n => {
                const size = getSize(n.id);
                const cx = x + size.width / 2;
                x += size.width + HORIZONTAL_GAP;
                return { id: n.id, x: cx, y: baseY, width: size.width, height: size.height };
            });
        }
    }, [nodes, routeStructure, nodeSizes]);

    const width = useMemo(() => {
        if (nodePositions.length === 0) return 320;
        const maxRight = Math.max(...nodePositions.map(p => p.x + p.width / 2));
        return Math.max(320, maxRight + SVG_PADDING);
    }, [nodePositions]);

    const height = useMemo(() => {
        if (nodePositions.length === 0) return 120;
        const maxBottom = Math.max(...nodePositions.map(p => p.y + p.height / 2));
        return maxBottom + SVG_PADDING;
    }, [nodePositions]);

    const handleNodeMouseEnter = useCallback((id: string) => setHoveredNodeId(id), []);
    const handleNodeMouseLeave = useCallback(() => setHoveredNodeId(undefined), []);
    const handleNodeClick = useCallback(
        (id: string) => {
            onNodeClick?.(id);
        },
        [onNodeClick]
    );

    const trailerIdToColor = useMemo(() => {
        const map = new Map<string, string>();
        pathsForEdges.forEach((path, i) => {
            map.set(path.trailerId, ROUTE_PATH_COLORS[i % ROUTE_PATH_COLORS.length]);
        });
        return map;
    }, [pathsForEdges]);

    if (nodes.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No route to display. Add events to the trip or select sources and destinations.
            </Typography>
        );
    }

    const posById = new Map(nodePositions.map(p => [p.id, p]));

    return (
        <Box sx={{ overflowX: 'auto', overflowY: 'visible', width: '100%', minWidth: 0 }}>
            <Box sx={{ minWidth: width }}>
                <svg width={width} height={height} style={{ display: 'block' }}>
                    {/* Edges: one per trailer per leg, colored by trailer; shared primitive */}
                    {legsWithTrailers.flatMap(leg => {
                        const fromPos = posById.get(leg.fromId);
                        const toPos = posById.get(leg.toId);
                        if (!fromPos || !toPos) return [];
                        const trailerIds = leg.trailerIds.length > 0 ? leg.trailerIds : ['__single__'];
                        const n = trailerIds.length;
                        const isHovered = hoveredLeg?.fromId === leg.fromId && hoveredLeg?.toId === leg.toId;

                        return trailerIds.map((trailerId, idx) => {
                            const offset = n === 1 ? 0 : (idx - (n - 1) / 2) * EDGE_OFFSET_SPACING;
                            const geometry = getEdgeGeometry(fromPos.x, fromPos.y, toPos.x, toPos.y, 0.5, offset);
                            const color = trailerIdToColor.get(trailerId) ?? '#757575';
                            const key = `${leg.fromId}\t${leg.toId}\t${trailerId}`;

                            return (
                                <EdgeWithArrow
                                    key={key}
                                    geometry={geometry}
                                    color={color}
                                    isHovered={isHovered}
                                    onMouseEnter={() => setHoveredLeg({ fromId: leg.fromId, toId: leg.toId })}
                                    onMouseLeave={() => setHoveredLeg(null)}
                                    onClick={
                                        trailerId !== '__single__' && onEdgeClick
                                            ? () => onEdgeClick(trailerId, leg.fromId, leg.toId)
                                            : undefined
                                    }
                                />
                            );
                        });
                    })}

                    {/* Nodes: rectangular so full names fit */}
                    {nodePositions.map(({ id, x, y, width: w, height: h }) => {
                        const node = nodes.find(n => n.id === id);
                        const label = node?.label ?? getNodeLabel(id);
                        const isHovered = hoveredNodeId === id;
                        const dateRange = node ? formatDateRange(node.startDate, node.endDate) : '';
                        const title = dateRange ? `${label}\n${dateRange}` : label;
                        const rx = x - w / 2;
                        const ry = y - h / 2;

                        return (
                            <g
                                key={id}
                                cursor={onNodeClick ? 'pointer' : 'default'}
                                onMouseEnter={() => handleNodeMouseEnter(id)}
                                onMouseLeave={handleNodeMouseLeave}
                                onClick={() => handleNodeClick(id)}
                            >
                                <title>{title}</title>
                                <rect
                                    x={rx}
                                    y={ry}
                                    width={w}
                                    height={h}
                                    rx={4}
                                    ry={4}
                                    fill={isHovered ? '#90caf9' : '#eee'}
                                    stroke="#1976d2"
                                    strokeWidth={2}
                                />
                                <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600} fill="#333">
                                    {label}
                                </text>
                            </g>
                        );
                    })}
                </svg>
                <Box
                    sx={{
                        height: TOOLTIP_LINE_HEIGHT,
                        mt: 0.5,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    {hoveredLeg &&
                        getTrailerName &&
                        (() => {
                            const leg = legsWithTrailers.find(l => l.fromId === hoveredLeg.fromId && l.toId === hoveredLeg.toId);
                            if (!leg) return null;
                            const trailerNames = leg.trailerIds.length > 0 ? leg.trailerIds.map(getTrailerName).join(', ') : null;
                            const legLabel = `${getNodeLabel(leg.fromId)} → ${getNodeLabel(leg.toId)}`;
                            return (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', lineHeight: `${TOOLTIP_LINE_HEIGHT}px` }}
                                >
                                    {legLabel}
                                    {trailerNames != null && ` • ${trailerNames}`}
                                </Typography>
                            );
                        })()}
                </Box>
            </Box>
        </Box>
    );
}
